import React, { useEffect, useState, useCallback, useMemo } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Settings, CreditCard, RefreshCcw, ExternalLink } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const DAYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

function niceDay(day) {
  if (!day) return 'Friday';
  return day.charAt(0).toUpperCase() + day.slice(1);
}

export default function SettingsTab({ groupId }) {
  const { t } = useTranslation();

  const [form, setForm] = useState({
    name: '',
    description: '',
    logo: '',
    membership_price: 0,
    membership_period: 1,
    currency: 'USD',
    visibility: 'public',
    require_subscription: false,
    max_capacity: '',
  });

  const [loading, setLoading] = useState(true);

  // Payouts (Stripe Connect)
  const [payoutLoading, setPayoutLoading] = useState(false);
  const [connect, setConnect] = useState({
    status: 'not_started', // not_started | pending | complete
    charges_enabled: false,
    payouts_enabled: false,
    requirements: null,
    weekly_anchor: 'friday',
    stripe_account_id: null,
  });

  const statusBadge = useMemo(() => {
    if (connect.status === 'complete') return { label: 'Enabled', variant: 'default' };
    if (connect.status === 'pending') return { label: 'Action needed', variant: 'outline' };
    return { label: 'Not set up', variant: 'destructive' };
  }, [connect.status]);

  const loadGroup = useCallback(
    async (signal) => {
      try {
        const g = await api.get(`/groups/${groupId}`);
        const group = g.data || {};
        if (!signal?.aborted) {
          setForm({
            name: group.name || '',
            description: group.description || '',
            logo: group.logo || '',
            membership_price: group.membership_price || 0,
            membership_period: group.membership_period || 1,
            currency: group.currency || 'USD',
            visibility: group.visibility || 'public',
            require_subscription: !!group.require_subscription,
            max_capacity:
              group.max_capacity !== undefined && group.max_capacity !== null
                ? group.max_capacity
                : '',
          });

          setConnect((c) => ({
            ...c,
            weekly_anchor: group.stripe_weekly_payout_day || c.weekly_anchor || 'friday',
            stripe_account_id: group.stripe_account_id || null,
          }));
        }
      } catch {
        if (!signal?.aborted) {
          toast.error(t('groupAdminTabs.settings.toastLoadError'));
        }
      } finally {
        if (!signal?.aborted) setLoading(false);
      }
    },
    [groupId, t]
  );

  const loadConnectStatus = useCallback(async () => {
    try {
      setPayoutLoading(true);
      const { data } = await api.get(`/connect/groups/${groupId}/status`);
      setConnect((c) => ({
        ...c,
        status: data?.status || 'not_started',
        charges_enabled: !!data?.charges_enabled,
        payouts_enabled: !!data?.payouts_enabled,
        requirements: data?.requirements || null,
      }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load payout status');
    } finally {
      setPayoutLoading(false);
    }
  }, [groupId]);

  useEffect(() => {
    const controller = new AbortController();
    const id = setTimeout(() => {
      loadGroup(controller.signal);
    }, 0);
    return () => {
      controller.abort();
      clearTimeout(id);
    };
  }, [loadGroup]);

  useEffect(() => {
    // Load connect status after group loads
    if (!loading) loadConnectStatus();
  }, [loading, loadConnectStatus]);

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        membership_price: Number(form.membership_price) || 0,
        membership_period: Number(form.membership_period) || 1,
        max_capacity:
          form.max_capacity === '' || form.max_capacity == null ? null : Number(form.max_capacity),
        require_subscription: !!form.require_subscription,
      };

      await api.put(`/groups/${groupId}`, payload);
      toast.success(t('groupAdminTabs.settings.toastSaveSuccess'));

      const controller = new AbortController();
      loadGroup(controller.signal);
    } catch (error) {
      toast.error(error?.response?.data?.detail || t('groupAdminTabs.settings.toastSaveError'));
    }
  };

  const ensureConnectAccount = async () => {
    try {
      setPayoutLoading(true);
      // creates or reuses
      const { data } = await api.post(`/connect/groups/${groupId}/account`);
      setConnect((c) => ({
        ...c,
        stripe_account_id: data?.stripe_account_id || c.stripe_account_id,
        status: data?.status || c.status,
      }));
      toast.success('Stripe account ready');
      await loadConnectStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create Stripe account');
    } finally {
      setPayoutLoading(false);
    }
  };

  const startOnboarding = async () => {
    try {
      setPayoutLoading(true);
      // make sure account exists
      if (!connect.stripe_account_id) {
        await ensureConnectAccount();
      }
      const { data } = await api.post(`/connect/groups/${groupId}/onboard`);
      if (data?.url) {
        window.location.href = data.url;
        return;
      }
      toast.error('No onboarding URL returned');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to start onboarding');
    } finally {
      setPayoutLoading(false);
    }
  };

  const saveWeeklyAnchor = async (weekly_anchor) => {
    try {
      setPayoutLoading(true);
      await api.post(`/connect/groups/${groupId}/payout-schedule`, { weekly_anchor });
      setConnect((c) => ({ ...c, weekly_anchor }));
      toast.success(`Weekly payout set to ${niceDay(weekly_anchor)}`);
      // also refresh status (optional)
      await loadConnectStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update payout schedule');
    } finally {
      setPayoutLoading(false);
    }
  };

  if (loading)
    return <p className="text-slate-600">{t('groupAdminTabs.settings.loading')}</p>;

  return (
    <div className="space-y-6">
      {/* PAGE TITLE */}
      <div>
        <h2 className="text-xl font-semibold">{t('groupAdminTabs.settings.title')}</h2>
        <p className="text-sm text-slate-500 mt-1">
          Manage your group settings and payouts.
        </p>
      </div>

      {/* PAYOUTS SECTION */}
      <Card className="border-slate-200">
        <CardHeader>
          <div className="flex items-start justify-between gap-3">
            <div>
              <CardTitle className="flex items-center gap-2">
                <CreditCard size={18} />
                Payouts (Stripe)
              </CardTitle>
              <CardDescription>
                Connect a bank account so your group receives weekly payouts.
              </CardDescription>
            </div>
            <Badge variant={statusBadge.variant}>{statusBadge.label}</Badge>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-3 gap-3">
            <div className="rounded-lg border p-3">
              <p className="text-xs text-slate-500">Charges enabled</p>
              <p className="font-medium">{connect.charges_enabled ? 'Yes' : 'No'}</p>
            </div>
            <div className="rounded-lg border p-3">
              <p className="text-xs text-slate-500">Payouts enabled</p>
              <p className="font-medium">{connect.payouts_enabled ? 'Yes' : 'No'}</p>
            </div>
            <div className="rounded-lg border p-3">
              <p className="text-xs text-slate-500">Weekly payout day</p>
              <p className="font-medium">{niceDay(connect.weekly_anchor)}</p>
            </div>
          </div>

          {connect.status !== 'complete' && (
            <div className="rounded-lg border bg-slate-50 p-3 text-sm text-slate-700">
              <p className="font-medium mb-1">Action needed</p>
              <p>
                Your group can’t receive money until onboarding is completed in Stripe.
              </p>
              {!!connect.requirements?.currently_due?.length && (
                <p className="text-xs text-slate-500 mt-2">
                  Required: {connect.requirements.currently_due.join(', ')}
                </p>
              )}
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            <Button
              onClick={ensureConnectAccount}
              variant="outline"
              disabled={payoutLoading}
              title="Create or reuse your group Stripe account"
            >
              {payoutLoading ? 'Working…' : 'Create Stripe account'}
            </Button>

            <Button onClick={startOnboarding} disabled={payoutLoading}>
              <ExternalLink size={16} className="mr-2" />
              Start onboarding
            </Button>

            <Button
              onClick={loadConnectStatus}
              variant="outline"
              disabled={payoutLoading}
            >
              <RefreshCcw size={16} className="mr-2" />
              Refresh status
            </Button>
          </div>

          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <Label>Weekly payout day</Label>
              <Select
                value={connect.weekly_anchor || 'friday'}
                onValueChange={saveWeeklyAnchor}
                disabled={payoutLoading || connect.status === 'not_started'}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select a day" />
                </SelectTrigger>
                <SelectContent>
                  {DAYS.map((d) => (
                    <SelectItem key={d} value={d}>
                      {niceDay(d)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-2">
                Stripe will send payouts weekly to the bank account added during onboarding.
              </p>
            </div>

            <div className="rounded-lg border p-3">
              <p className="text-xs text-slate-500">Tip</p>
              <p className="text-sm text-slate-700">
                If the admin changes the bank info, they do it inside Stripe during onboarding.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* GROUP SETTINGS FORM (reorganized into cards) */}
      <form onSubmit={handleUpdate} className="grid lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>General</CardTitle>
            <CardDescription>Basic details for your group.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <Label>{t('groupAdminTabs.settings.name')}</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                required
              />
            </div>

            <div>
              <Label>{t('groupAdminTabs.settings.description')}</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              />
            </div>

            <div>
              <Label>{t('groupAdminTabs.settings.logo')}</Label>
              <Input
                value={form.logo}
                onChange={(e) => setForm((f) => ({ ...f, logo: e.target.value }))}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Membership</CardTitle>
            <CardDescription>Paid membership settings for your group.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{t('groupAdminTabs.settings.membershipPrice')}</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={form.membership_price}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, membership_price: e.target.value }))
                  }
                />
              </div>

              <div>
                <Label>{t('groupAdminTabs.settings.membershipPeriod')}</Label>
                <Input
                  type="number"
                  value={form.membership_period}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, membership_period: e.target.value }))
                  }
                />
              </div>
            </div>

            <div>
              <Label>{t('groupAdminTabs.settings.currency')}</Label>
              <Input
                value={form.currency}
                onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value }))}
              />
            </div>

            <div className="flex items-center justify-between border rounded-lg px-3 py-2">
              <div>
                <Label className="font-medium">
                  {t('groupAdminTabs.settings.requireSubscription')}
                </Label>
                <p className="text-xs text-slate-500">
                  {t('groupAdminTabs.settings.requireSubscriptionHint')}
                </p>
              </div>
              <Switch
                checked={!!form.require_subscription}
                onCheckedChange={(checked) =>
                  setForm((f) => ({ ...f, require_subscription: checked }))
                }
              />
            </div>
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Visibility & Capacity</CardTitle>
            <CardDescription>Who can see the group and how many members it can have.</CardDescription>
          </CardHeader>
          <CardContent className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>{t('groupAdminTabs.settings.visibility')}</Label>
              <Select
                value={form.visibility}
                onValueChange={(v) => setForm((f) => ({ ...f, visibility: v }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder={t('groupAdminTabs.settings.visibility')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="public">
                    {t('groupAdminTabs.settings.visibilityPublic')}
                  </SelectItem>
                  <SelectItem value="private">
                    {t('groupAdminTabs.settings.visibilityPrivate')}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>{t('groupAdminTabs.settings.maxMembers')}</Label>
              <Input
                type="number"
                min="1"
                value={form.max_capacity}
                onChange={(e) => setForm((f) => ({ ...f, max_capacity: e.target.value }))}
              />
            </div>

            <div className="md:col-span-2 flex justify-end pt-2">
              <Button type="submit">
                <Settings size={16} className="mr-2" />
                {t('groupAdminTabs.settings.save')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </form>
    </div>
  );
}
